// A resizable, persisted side rail — the primitive behind the project-tree rail
// and the Creator Studio step rail.
//
// Why a CSS custom property rather than React state driving `style={{width}}`:
// a drag fires mousemove at frame rate, and re-rendering a 300-row tree (or a
// 200-step list) on each one drops frames. So the drag writes the variable
// straight to the DOM — layout follows the pointer with no reconciliation — and
// only *commits* to state + localStorage on mouseup.
//
// Consequence: anywhere the value must stay live during a drag, read it in CSS
// as `var(--…)`, never from the hook's `width` return value.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface RailSpec {
  /** localStorage key. House convention: `worksnip:*`. */
  storageKey: string;
  /** CSS custom property published on `<html>`, e.g. `--ws-tree-w`. */
  cssVar: string;
  defaultWidth: number;
  min: number;
  /** Leave at least this much for the rest of the page, so the rail can't eat the app. */
  minBody: number;
}

export interface Rail {
  spec: RailSpec;
  applyVar: (px: number) => void;
  useWidth: () => {
    width: number;
    setWidth: (next: number) => void;
    resetToDefault: () => void;
    widthRef: React.MutableRefObject<number>;
  };
}

/**
 * Builds a rail and publishes its stored width to the DOM immediately — before
 * the first paint, so nothing flashes at the fallback width and a collapsed rail
 * (whose resizer never mounts) still has the right value waiting for when it
 * expands. Call once per rail at module scope.
 */
export function createRail(spec: RailSpec): Rail {
  const { storageKey, cssVar, defaultWidth, min, minBody } = spec;

  function clampTo(next: number): number {
    const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const max = Math.max(min + 1, vw - minBody);
    return Math.max(min, Math.min(next, max));
  }

  function readStored(): number {
    if (typeof window === 'undefined') return defaultWidth;
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed >= min ? parsed : defaultWidth;
  }

  function applyVar(px: number): void {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(cssVar, `${Math.round(px)}px`);
  }

  applyVar(readStored());

  function useWidth() {
    const [width, setWidthState] = useState<number>(readStored);
    // Mirror in a ref so the drag handler reads the latest value without
    // re-binding its listeners on every render.
    const widthRef = useRef(width);
    widthRef.current = width;

    const setWidth = useCallback((next: number) => {
      const clamped = clampTo(next);
      widthRef.current = clamped;
      setWidthState(clamped);
      applyVar(clamped);
      try { localStorage.setItem(storageKey, String(Math.round(clamped))); } catch { /* private mode */ }
    }, []);

    // A rail sized against a wide window must not stay wider than a narrow one.
    useEffect(() => {
      const onResize = () => {
        const clamped = clampTo(widthRef.current);
        if (clamped !== widthRef.current) setWidth(clamped);
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, [setWidth]);

    const resetToDefault = useCallback(() => setWidth(defaultWidth), [setWidth]);

    return { width, setWidth, resetToDefault, widthRef };
  }

  return { spec, applyVar, useWidth };
}
